import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { loadImage, loadImages } from '../utils/imageLoad';
import { getPageLoadConfig } from '../utils/pageCriticalImages';

const PageReadyContext = createContext(null);

function PageReadyProvider({ pathname, children }) {
  const [readyPathname, setReadyPathname] = useState(null);
  const extraSourcesRef = useRef(new Set());
  const [extraVersion, setExtraVersion] = useState(0);
  const loadConfig = useMemo(() => getPageLoadConfig(pathname), [pathname]);

  const registerImages = useCallback((sources) => {
    const normalized = sources.filter(Boolean);
    if (normalized.length === 0) {
      return;
    }

    let changed = false;
    normalized.forEach((source) => {
      if (!extraSourcesRef.current.has(source)) {
        extraSourcesRef.current.add(source);
        changed = true;
      }
    });

    if (changed) {
      setExtraVersion((version) => version + 1);
    }
  }, []);

  useEffect(() => {
    extraSourcesRef.current = new Set();
    setExtraVersion(0);
    setReadyPathname(null);
  }, [pathname]);

  useEffect(() => {
    let cancelled = false;
    const extraSources = [...extraSourcesRef.current];
    const blockingSources = [...new Set([...loadConfig.urls, ...extraSources])];

    setReadyPathname(null);

    loadImages(blockingSources).then(() => {
      if (!cancelled) {
        setReadyPathname(pathname);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [pathname, extraVersion, loadConfig.urls]);

  useEffect(() => {
    if (readyPathname !== pathname || !loadConfig.backgroundUrls.length) {
      return undefined;
    }

    loadImages(loadConfig.backgroundUrls, { concurrency: 8 });

    return undefined;
  }, [readyPathname, pathname, loadConfig.backgroundUrls]);

  const isPageReady = readyPathname === pathname;

  const value = useMemo(
    () => ({
      isPageReady,
      loadConfig,
      registerImages,
      preloadImage: loadImage,
    }),
    [isPageReady, loadConfig, registerImages],
  );

  return (
    <PageReadyContext.Provider value={value}>
      {children}
    </PageReadyContext.Provider>
  );
}

function usePageReady() {
  const context = useContext(PageReadyContext);

  if (!context) {
    throw new Error('usePageReady must be used within PageReadyProvider');
  }

  return context;
}

function useRegisterPageImages(sources) {
  const { registerImages } = usePageReady();
  const sourceKey = JSON.stringify(sources);

  useEffect(() => {
    registerImages(sources);
  }, [registerImages, sourceKey, sources]);
}

export { PageReadyProvider, usePageReady, useRegisterPageImages };
